import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { useAppStore } from '@/store';
import { useProfile } from '@/hooks/useProfile';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { ArrowUpRight, Shield } from 'lucide-react';

export const SettingsPanel = () => {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { currentTier, loading: subscriptionLoading } = useSubscription();
  const { textSize, setTextSize } = useAppStore();
  const [portalLoading, setPortalLoading] = useState(false);

  const isAdmin = profile?.role === 'admin';

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session');
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Could not open billing portal', variant: 'destructive' });
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">Manage your account and accessibility preferences.</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/')}>Back</Button>
      </div>

      <Tabs defaultValue="general" className="mt-6 w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="general">General</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          {isAdmin ? (
            <Card className="border-primary/30 bg-primary/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Shield className="h-4 w-4" />
                  Admin tools moved
                </CardTitle>
                <CardDescription>
                  Models, news, lobby, logs, analytics, orders, and marketing now live in the admin dashboard.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="outline">
                  <Link to="/admin" className="inline-flex items-center gap-2">
                    Open admin dashboard
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : null}

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Subscription</CardTitle>
                <CardDescription>Manage your billing and subscription plan.</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-sm">
                  Your current plan is{' '}
                  <span className="font-semibold">
                    {subscriptionLoading ? 'Loading...' : currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}
                  </span>
                </p>
                <Button className="w-full" onClick={handleManageSubscription} disabled={subscriptionLoading || portalLoading}>
                  {portalLoading ? 'Redirecting...' : currentTier === 'free' ? 'Upgrade Plan' : 'Manage Subscription'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>AI Center</CardTitle>
                <CardDescription>
                  Trust controls, privacy, API integrations, sealed memory, and MapleOS updates now live in AI Center.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/desktop/start" className="inline-flex items-center gap-2">
                    Open MapleOS
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Accessibility</CardTitle>
                <CardDescription>Customize readability for the interface.</CardDescription>
              </CardHeader>
              <CardContent>
                <Label className="mb-3 block">Text Size</Label>
                <RadioGroup value={textSize} onValueChange={(value) => setTextSize(value as 'small' | 'medium' | 'large')}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="small" id="small" />
                    <Label htmlFor="small">Small</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="medium" id="medium" />
                    <Label htmlFor="medium">Medium</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="large" id="large" />
                    <Label htmlFor="large">Large</Label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const Settings = () => {
  return (
    <MainLayout>
      <div className="container mx-auto max-w-7xl px-4 py-6">
        <SettingsPanel />
      </div>
    </MainLayout>
  );
};

export default Settings;
